/**
 * Edit User Modal Component
 * Allows admin to create new user or update existing user information
 *
 * Supports 2 modes:
 * - CREATE mode: user === null → Create new user with email + password
 * - EDIT mode: user !== null → Edit existing user info
 */

import React, { useState, useEffect, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { API_URL } from '../../../services/api';
import { USER_CATEGORIES } from '../../../constants/userCategories';
import {
  FiX,
  FiUser,
  FiMail,
  FiPhone,
  FiShield,
  FiAlertCircle,
  FiSave,
  FiLock,
  FiUserPlus,
  FiUsers,
} from 'react-icons/fi';

interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  role: 'USER' | 'COMPANY_OWNER' | 'ADMIN' | 'SUPER_ADMIN' | 'MEMBER';
  status: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED' | 'BANNED';
  userCategory?: string; // User category field
}

interface EditUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: User | null;
  onSuccess?: () => void;
}

interface UserUpdateData {
  email?: string;
  password?: string;
  confirmPassword?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  role?: string;
  status?: string;
  userCategory?: string; // User category field
}

const EditUserModal: React.FC<EditUserModalProps> = ({
  isOpen,
  onClose,
  user,
  onSuccess,
}) => {
  // Determine mode (CREATE or EDIT)
  const isCreateMode = user === null;

  // Form state
  const [formData, setFormData] = useState<UserUpdateData>({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    phone: '',
    role: 'MEMBER',
    status: 'ACTIVE',
    userCategory: 'individual', // Default category
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Initialize form data when user changes or modal opens
  useEffect(() => {
    if (isCreateMode) {
      // CREATE mode: Reset to empty form
      setFormData({
        email: '',
        password: '',
        confirmPassword: '',
        firstName: '',
        lastName: '',
        phone: '',
        role: 'MEMBER',
        status: 'ACTIVE',
        userCategory: 'individual', // Default category
      });
      setError(null);
    } else if (user) {
      // EDIT mode: Populate with user data
      setFormData({
        email: user.email || '',
        password: '', // Never populate password
        confirmPassword: '',
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        phone: user.phone || '',
        role: user.role || 'MEMBER',
        status: user.status || 'ACTIVE',
        userCategory: user.userCategory || 'individual', // Populate current category
      });
      setError(null);
    }
  }, [user, isCreateMode, isOpen]);

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setError(null);
    setIsSubmitting(true);

    try {
      // Get JWT token from localStorage
      const token = localStorage.getItem('access_token');
      if (!token) {
        throw new Error('กรุณาเข้าสู่ระบบก่อน');
      }



      if (isCreateMode) {
        // ===== CREATE MODE =====
        // Validate required fields
        if (!formData.email || !formData.email.trim()) {
          throw new Error('กรุณากรอกอีเมล');
        }
        if (!formData.password || formData.password.length < 8) {
          throw new Error('รหัสผ่านต้องมีอย่างน้อย 8 ตัวอักษร');
        }
        if (formData.password !== formData.confirmPassword) {
          throw new Error('รหัสผ่านไม่ตรงกัน');
        }

        // DEBUG: Log formData before submitting (will be removed after testing)
        console.log('🐛 DEBUG CREATE MODE - formData:', {
          userCategory: formData.userCategory,
          email: formData.email,
          role: formData.role,
          fullFormData: formData
        });

        // Prepare create data
        // FIX: Handle empty string properly - if empty, default to 'individual'
        const userCategory = formData.userCategory && formData.userCategory.trim() !== ''
          ? formData.userCategory.trim().toUpperCase()
          : 'INDIVIDUAL';

        const createData = {
          email: formData.email.trim(),
          password: formData.password,
          firstName: formData.firstName?.trim() || '',
          lastName: formData.lastName?.trim() || '',
          phone: formData.phone?.trim() || '',
          role: formData.role || 'MEMBER',
          user_category: userCategory, // Use cleaned and transformed category
        };

        // DEBUG: Log final createData (will be removed after testing)
        console.log('🐛 DEBUG CREATE MODE - createData to send:', createData);

        // Call POST /api/admin/users
        const response = await fetch(`${API_URL}/api/admin/users`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify(createData),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || 'ไม่สามารถสร้างผู้ใช้ได้');
        }

        // Success
        toast.success('เพิ่มผู้ใช้เรียบร้อยแล้ว');
        onClose();

        // Call success callback
        if (onSuccess) {
          onSuccess();
        }

      } else {
        // ===== EDIT MODE =====
        if (!user) return;

        // Prepare update data (only send changed fields)
        const updateData: any = {};
        if (formData.firstName !== user.firstName) updateData.firstName = formData.firstName;
        if (formData.lastName !== user.lastName) updateData.lastName = formData.lastName;
        if (formData.phone !== user.phone) updateData.phone = formData.phone;

        // Transform userCategory: camelCase → snake_case AND lowercase → UPPERCASE
        // Frontend: "corporate" → Backend: "CORPORATE" (via "user_category")
        if (formData.userCategory !== user.userCategory) {
          updateData.user_category = formData.userCategory?.toUpperCase();
        }

        // Update basic user info
        if (Object.keys(updateData).length > 0) {
          const response = await fetch(`${API_URL}/api/admin/users/${user.id}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify(updateData),
          });

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.detail || 'ไม่สามารถอัปเดตข้อมูลผู้ใช้ได้');
          }
        }

        // Update role if changed
        if (formData.role !== user.role) {
          const roleResponse = await fetch(`${API_URL}/api/admin/users/${user.id}/role`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({ role: formData.role }),
          });

          if (!roleResponse.ok) {
            const errorData = await roleResponse.json();
            throw new Error(errorData.detail || 'ไม่สามารถเปลี่ยนบทบาทได้');
          }
        }

        // Update status if changed
        if (formData.status !== user.status) {
          let statusEndpoint = '';
          if (formData.status === 'ACTIVE') {
            statusEndpoint = `${API_URL}/api/admin/users/${user.id}/activate`;
          } else {
            statusEndpoint = `${API_URL}/api/admin/users/${user.id}/deactivate`;
          }

          const statusResponse = await fetch(statusEndpoint, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
          });

          if (!statusResponse.ok) {
            const errorData = await statusResponse.json();
            throw new Error(errorData.detail || 'ไม่สามารถเปลี่ยนสถานะได้');
          }
        }

        // Success
        toast.success('อัปเดตข้อมูลผู้ใช้เรียบร้อยแล้ว');
        onClose();

        // Call success callback
        if (onSuccess) {
          onSuccess();
        }
      }
    } catch (err: any) {
      console.error('Failed to submit user form:', err);
      setError(err.message || 'เกิดข้อผิดพลาดในการบันทึกข้อมูล');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle close
  const handleClose = () => {
    if (!isSubmitting) {
      setError(null);
      onClose();
    }
  };

  // Handle input change
  const handleInputChange = (field: keyof UserUpdateData, value: string) => {
    // DEBUG: Log userCategory changes (will be removed after testing)
    if (field === 'userCategory') {
      console.log('🐛 DEBUG handleInputChange - userCategory changed:', {
        oldValue: formData.userCategory,
        newValue: value,
        valueType: typeof value,
        isEmpty: value === '',
        isCreateMode
      });
    }

    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        {/* Backdrop */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
        </Transition.Child>

        {/* Modal Container */}
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white shadow-xl transition-all">
                {/* Modal Header */}
                <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
                  <div className="flex items-center space-x-3">
                    <div className={`flex h-10 w-10 items-center justify-center rounded-full ${
                      isCreateMode ? 'bg-green-100' : 'bg-blue-100'
                    }`}>
                      {isCreateMode ? (
                        <FiUserPlus className="h-5 w-5 text-green-600" />
                      ) : (
                        <FiUser className="h-5 w-5 text-blue-600" />
                      )}
                    </div>
                    <Dialog.Title className="text-lg font-semibold text-gray-900">
                      {isCreateMode ? 'เพิ่มผู้ใช้ใหม่' : 'แก้ไขข้อมูลผู้ใช้'}
                    </Dialog.Title>
                  </div>
                  <button
                    onClick={handleClose}
                    disabled={isSubmitting}
                    className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FiX className="h-5 w-5" />
                  </button>
                </div>

                {/* Modal Body */}
                <form onSubmit={handleSubmit}>
                  <div className="px-6 py-4 space-y-4">
                    {/* Email Field */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <FiMail className="inline h-4 w-4 mr-1" />
                        อีเมล {isCreateMode && <span className="text-red-500">*</span>}
                      </label>
                      <input
                        type="email"
                        value={formData.email || ''}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        disabled={!isCreateMode || isSubmitting}
                        required={isCreateMode}
                        placeholder={isCreateMode ? "example@email.com" : ""}
                        className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed ${
                          !isCreateMode ? 'bg-gray-50 text-gray-500' : ''
                        }`}
                      />
                      {!isCreateMode && (
                        <p className="text-xs text-gray-500 mt-1">
                          ไม่สามารถแก้ไขอีเมลได้
                        </p>
                      )}
                    </div>

                    {/* Password Fields (CREATE mode only) */}
                    {isCreateMode && (
                      <>
                        {/* Password */}
                        <div>
                          <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                            <FiLock className="inline h-4 w-4 mr-1" />
                            รหัสผ่าน <span className="text-red-500">*</span>
                          </label>
                          <input
                            id="password"
                            type="password"
                            value={formData.password || ''}
                            onChange={(e) => handleInputChange('password', e.target.value)}
                            disabled={isSubmitting}
                            required
                            placeholder="อย่างน้อย 8 ตัวอักษร"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                            minLength={8}
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            รหัสผ่านต้องมีอย่างน้อย 8 ตัวอักษร
                          </p>
                        </div>

                        {/* Confirm Password */}
                        <div>
                          <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                            <FiLock className="inline h-4 w-4 mr-1" />
                            ยืนยันรหัสผ่าน <span className="text-red-500">*</span>
                          </label>
                          <input
                            id="confirmPassword"
                            type="password"
                            value={formData.confirmPassword || ''}
                            onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                            disabled={isSubmitting}
                            required
                            placeholder="ยืนยันรหัสผ่าน"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                            minLength={8}
                          />
                          {formData.password && formData.confirmPassword && formData.password !== formData.confirmPassword && (
                            <p className="text-xs text-red-600 mt-1">
                              รหัสผ่านไม่ตรงกัน
                            </p>
                          )}
                        </div>
                      </>
                    )}

                    {/* First Name */}
                    <div>
                      <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                        ชื่อ
                      </label>
                      <input
                        id="firstName"
                        type="text"
                        value={formData.firstName}
                        onChange={(e) => handleInputChange('firstName', e.target.value)}
                        disabled={isSubmitting}
                        placeholder="กรอกชื่อ"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                        maxLength={100}
                      />
                    </div>

                    {/* Last Name */}
                    <div>
                      <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                        นามสกุล
                      </label>
                      <input
                        id="lastName"
                        type="text"
                        value={formData.lastName}
                        onChange={(e) => handleInputChange('lastName', e.target.value)}
                        disabled={isSubmitting}
                        placeholder="กรอกนามสกุล"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                        maxLength={100}
                      />
                    </div>

                    {/* Phone */}
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                        <FiPhone className="inline h-4 w-4 mr-1" />
                        เบอร์โทรศัพท์
                      </label>
                      <input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                        disabled={isSubmitting}
                        placeholder="กรอกเบอร์โทรศัพท์"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                        maxLength={20}
                      />
                    </div>

                    {/* User Category */}
                    <div>
                      <label htmlFor="userCategory" className="block text-sm font-medium text-gray-700 mb-2">
                        <FiUsers className="inline h-4 w-4 mr-1" />
                        ประเภทผู้ใช้
                      </label>
                      <select
                        id="userCategory"
                        value={formData.userCategory || 'individual'}
                        onChange={(e) => handleInputChange('userCategory', e.target.value)}
                        disabled={isSubmitting}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed bg-white"
                      >
                        {USER_CATEGORIES.map((category) => (
                          <option key={category.value} value={category.value}>
                            {category.icon} {category.label}
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        Default: บุคคลทั่วไป
                      </p>
                    </div>

                    {/* Role */}
                    <div>
                      <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-2">
                        <FiShield className="inline h-4 w-4 mr-1" />
                        บทบาท
                      </label>
                      <select
                        id="role"
                        value={formData.role}
                        onChange={(e) => handleInputChange('role', e.target.value)}
                        disabled={isSubmitting}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed bg-white"
                      >
                        <option value="MEMBER">สมาชิก (Member)</option>
                        <option value="ADMIN">ผู้ดูแลระบบ (Admin)</option>
                      </select>
                    </div>

                    {/* Status */}
                    <div>
                      <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-2">
                        สถานะ
                      </label>
                      <select
                        id="status"
                        value={formData.status}
                        onChange={(e) => handleInputChange('status', e.target.value)}
                        disabled={isSubmitting}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed bg-white"
                      >
                        <option value="ACTIVE">ใช้งานได้</option>
                        <option value="INACTIVE">ไม่ใช้งาน</option>
                        <option value="SUSPENDED">ถูกระงับ</option>
                        <option value="BANNED">ถูกแบน</option>
                      </select>
                    </div>

                    {/* Info Box */}
                    <div className={`flex items-start space-x-3 p-4 border rounded-lg ${
                      isCreateMode
                        ? 'bg-green-50 border-green-200'
                        : 'bg-blue-50 border-blue-200'
                    }`}>
                      <FiAlertCircle className={`h-5 w-5 flex-shrink-0 mt-0.5 ${
                        isCreateMode ? 'text-green-600' : 'text-blue-600'
                      }`} />
                      <div className="text-sm">
                        <p className="font-medium mb-1">
                          {isCreateMode ? 'เพิ่มผู้ใช้ใหม่' : 'ข้อมูลสำคัญ'}
                        </p>
                        <p className={isCreateMode ? 'text-green-700' : 'text-blue-700'}>
                          {isCreateMode
                            ? 'ผู้ใช้ใหม่จะสามารถเข้าสู่ระบบได้ทันทีหลังสร้างบัญชี'
                            : 'การเปลี่ยนแปลงข้อมูลจะมีผลทันทีหลังจากบันทึก'
                          }
                        </p>
                      </div>
                    </div>

                    {/* Error Message */}
                    {error && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="flex items-start space-x-2 p-3 bg-red-50 border border-red-200 rounded-lg"
                      >
                        <FiAlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-red-700">{error}</p>
                      </motion.div>
                    )}
                  </div>

                  {/* Modal Footer */}
                  <div className="flex items-center justify-end space-x-3 border-t border-gray-200 px-6 py-4 bg-gray-50">
                    <button
                      type="button"
                      onClick={handleClose}
                      disabled={isSubmitting}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      ยกเลิก
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className={`px-6 py-2 text-sm font-medium text-white rounded-lg transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none flex items-center space-x-2 ${
                        isCreateMode
                          ? 'bg-green-600 hover:bg-green-700'
                          : 'bg-blue-600 hover:bg-blue-700'
                      }`}
                    >
                      {isSubmitting ? (
                        <>
                          <svg
                            className="animate-spin h-4 w-4"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          <span>{isCreateMode ? 'กำลังสร้าง...' : 'กำลังบันทึก...'}</span>
                        </>
                      ) : (
                        <>
                          {isCreateMode ? (
                            <FiUserPlus className="h-4 w-4" />
                          ) : (
                            <FiSave className="h-4 w-4" />
                          )}
                          <span>{isCreateMode ? 'เพิ่มผู้ใช้' : 'บันทึก'}</span>
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default EditUserModal;
